import { relations, sql } from 'drizzle-orm'
import {
  boolean,
  check,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  real,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core'
import { tenants } from './schema/tenants'

// Enums
export const userRoleEnum = pgEnum('user_role', ['student', 'instructor', 'admin', 'superadmin'])
export const lessonTypeEnum = pgEnum('lesson_type', [
  'video',
  'text',
  'quiz',
  'assignment',
  'external',
])
export const enrollmentStatusEnum = pgEnum('enrollment_status', [
  'active',
  'suspended',
  'completed',
])

// Users table
export const users = pgTable(
  'users',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    email: text('email').notNull().unique(),
    name: text('name').notNull(),
    password: text('password').notNull(),
    role: userRoleEnum('role').notNull().default('student'),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    updatedAt: timestamp('updated_at').notNull().defaultNow(),
  },
  (table) => ({
    tenantIdIdx: index('users_tenant_id_idx').on(table.tenantId),
    tenantEmailIdx: index('users_tenant_email_idx').on(table.tenantId, table.email),
  }),
)

// Courses table
export const courses = pgTable(
  'courses',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    title: text('title').notNull(),
    description: text('description').notNull(),
    category: text('category').notNull(),
    level: text('level').notNull(),
    duration: text('duration').notNull(),
    instructorId: uuid('instructor_id').references(() => users.id),
    imageUrl: text('image_url'),
    isPublished: boolean('is_published').default(false).notNull(),
    markdownSummary: text('markdown_summary'),
    evaluationPrompt: text('evaluation_prompt'),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    updatedAt: timestamp('updated_at').notNull().defaultNow(),
  },
  (table) => ({
    tenantIdIdx: index('courses_tenant_id_idx').on(table.tenantId),
  }),
)

// Tags table
export const tags = pgTable(
  'tags',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    name: text('name').notNull(),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    tenantIdIdx: index('tags_tenant_id_idx').on(table.tenantId),
    tenantNameUnique: unique('tags_tenant_name_unique').on(table.tenantId, table.name),
  }),
)

// Lessons table
export const lessons = pgTable(
  'lessons',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    title: text('title').notNull(),
    description: text('description'),
    type: lessonTypeEnum('type').notNull(),
    content: text('content'),
    videoBlobName: text('video_blob_name'),
    externalUrl: text('external_url'),
    videoTotalDurationSeconds: integer('video_total_duration_seconds'),
    level: text('level').notNull(),
    downloadable: boolean('downloadable').default(false),
    videoOriginalFilename: text('video_original_filename'),
    videoMimeType: text('video_mime_type'),
    videoFilesizeBytes: integer('video_filesize_bytes'),
    isPublished: boolean('is_published').default(false).notNull(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    updatedAt: timestamp('updated_at').notNull().defaultNow(),
  },
  (table) => ({
    tenantIdIdx: index('lessons_tenant_id_idx').on(table.tenantId),
  }),
)

// Lesson-Tag relation table
export const lessonTags = pgTable(
  'lesson_tags',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    lessonId: uuid('lesson_id')
      .references(() => lessons.id)
      .notNull()
      .unique(),
    tagId: uuid('tag_id')
      .references(() => tags.id)
      .notNull(),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    tenantIdIdx: index('lesson_tags_tenant_id_idx').on(table.tenantId),
  }),
)

// Course-Tag relation table
export const courseTags = pgTable(
  'course_tags',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    courseId: uuid('course_id')
      .references(() => courses.id)
      .notNull(),
    tagId: uuid('tag_id')
      .references(() => tags.id)
      .notNull(),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    tenantIdIdx: index('course_tags_tenant_id_idx').on(table.tenantId),
  }),
)

// Enrollments table
export const enrollments = pgTable(
  'enrollments',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id')
      .references(() => users.id)
      .notNull(),
    courseId: uuid('course_id')
      .references(() => courses.id)
      .notNull(),
    enrolledAt: timestamp('enrolled_at').notNull().defaultNow(),
    status: enrollmentStatusEnum('status').default('active').notNull(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    tenantIdIdx: index('enrollments_tenant_id_idx').on(table.tenantId),
  }),
)

// Completions table
export const completions = pgTable(
  'completions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id')
      .references(() => users.id)
      .notNull(),
    lessonId: uuid('lesson_id')
      .references(() => lessons.id)
      .notNull(),
    completedAt: timestamp('completed_at').notNull(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    tenantIdIdx: index('completions_tenant_id_idx').on(table.tenantId),
  }),
)

// Reviews table
export const reviews = pgTable(
  'reviews',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id')
      .references(() => users.id)
      .notNull(),
    courseId: uuid('course_id')
      .references(() => courses.id)
      .notNull(),
    rating: integer('rating').notNull(),
    comment: text('comment'),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    tenantIdIdx: index('reviews_tenant_id_idx').on(table.tenantId),
  }),
)

// UserLessonProgress table
export const userLessonProgress = pgTable(
  'user_lesson_progress',
  {
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    lessonId: uuid('lesson_id')
      .notNull()
      .references(() => lessons.id, { onDelete: 'cascade' }),
    totalPlayedSeconds: integer('total_played_seconds').default(0).notNull(),
    playCount: integer('play_count').default(0).notNull(),
    lastPlayedAt: timestamp('last_played_at', { mode: 'date' }).notNull().defaultNow(),
    isCompleted: boolean('is_completed').default(false).notNull(),
    updatedAt: timestamp('updated_at', { mode: 'date' }).notNull().defaultNow(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.userId, table.lessonId] }),
    tenantIdIdx: index('user_lesson_progress_tenant_id_idx').on(table.tenantId),
  }),
)

// CourseLessonOrders table
export const courseLessonOrders = pgTable(
  'course_lesson_orders',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    courseId: uuid('course_id')
      .notNull()
      .references(() => courses.id, { onDelete: 'cascade' }),
    lessonId: uuid('lesson_id')
      .notNull()
      .references(() => lessons.id, { onDelete: 'cascade' }),
    sortOrder: integer('sort_order').notNull(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    updatedAt: timestamp('updated_at').notNull().defaultNow(),
  },
  (table) => ({
    tenantIdIdx: index('course_lesson_orders_tenant_id_idx').on(table.tenantId),
    uniqueCourseLessonIdx: unique('course_lesson_orders_unique').on(
      table.courseId,
      table.lessonId,
      table.tenantId,
    ),
  }),
)

// Relations
export const usersRelations = relations(users, ({ many }) => ({
  enrollments: many(enrollments),
  completions: many(completions),
  reviews: many(reviews),
  courses: many(courses),
  userLessonProgress: many(userLessonProgress),
}))

export const coursesRelations = relations(courses, ({ one, many }) => ({
  instructor: one(users, { fields: [courses.instructorId], references: [users.id] }),
  courseTags: many(courseTags),
  enrollments: many(enrollments),
  reviews: many(reviews),
  courseLessonOrders: many(courseLessonOrders),
}))

export const courseLessonOrdersRelations = relations(courseLessonOrders, ({ one }) => ({
  course: one(courses, { fields: [courseLessonOrders.courseId], references: [courses.id] }),
  lesson: one(lessons, { fields: [courseLessonOrders.lessonId], references: [lessons.id] }),
}))

export const lessonsRelations = relations(lessons, ({ one, many }) => ({
  lessonTag: one(lessonTags, { fields: [lessons.id], references: [lessonTags.lessonId] }),
  completions: many(completions),
  userLessonProgress: many(userLessonProgress),
}))

export const tagsRelations = relations(tags, ({ many }) => ({
  lessonTags: many(lessonTags),
  courseTags: many(courseTags),
}))

export const lessonTagsRelations = relations(lessonTags, ({ one }) => ({
  lesson: one(lessons, { fields: [lessonTags.lessonId], references: [lessons.id] }),
  tag: one(tags, { fields: [lessonTags.tagId], references: [tags.id] }),
}))

export const courseTagsRelations = relations(courseTags, ({ one }) => ({
  course: one(courses, { fields: [courseTags.courseId], references: [courses.id] }),
  tag: one(tags, { fields: [courseTags.tagId], references: [tags.id] }),
}))

export const enrollmentsRelations = relations(enrollments, ({ one }) => ({
  user: one(users, { fields: [enrollments.userId], references: [users.id] }),
  course: one(courses, { fields: [enrollments.courseId], references: [courses.id] }),
}))

export const completionsRelations = relations(completions, ({ one }) => ({
  user: one(users, { fields: [completions.userId], references: [users.id] }),
  lesson: one(lessons, { fields: [completions.lessonId], references: [lessons.id] }),
}))

export const reviewsRelations = relations(reviews, ({ one }) => ({
  user: one(users, { fields: [reviews.userId], references: [users.id] }),
  course: one(courses, { fields: [reviews.courseId], references: [courses.id] }),
}))

export const userLessonProgressRelations = relations(userLessonProgress, ({ one }) => ({
  user: one(users, { fields: [userLessonProgress.userId], references: [users.id] }),
  lesson: one(lessons, { fields: [userLessonProgress.lessonId], references: [lessons.id] }),
}))

// Re-export tenants
export { tenants, userTenants } from './schema/tenants'

// ── Analytics tables ────────────────────────────────────────────────────────

export const lessonFeedback = pgTable(
  'lesson_feedback',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    lessonId: uuid('lesson_id')
      .notNull()
      .references(() => lessons.id, { onDelete: 'cascade' }),
    rating: integer('rating').notNull(),
    comment: text('comment'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    ratingCheck: check('rating_check', sql`rating >= 1 AND rating <= 5`),
    tenantIdIdx: index('lesson_feedback_tenant_id_idx').on(table.tenantId),
    uniqueUserLessonRating: uniqueIndex('unique_user_lesson_rating_idx').on(
      table.userId,
      table.lessonId,
      table.tenantId,
    ),
  }),
)

export const lessonQuestions = pgTable(
  'lesson_questions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    lessonId: uuid('lesson_id')
      .notNull()
      .references(() => lessons.id, { onDelete: 'cascade' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    question: text('question').notNull(),
    videoTimestamp: integer('video_timestamp'),
    isResolved: boolean('is_resolved').default(false).notNull(),
    upvotes: integer('upvotes').default(0).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    tenantIdIdx: index('lesson_questions_tenant_id_idx').on(table.tenantId),
  }),
)

export const lessonAnswers = pgTable(
  'lesson_answers',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    questionId: uuid('question_id')
      .notNull()
      .references(() => lessonQuestions.id, { onDelete: 'cascade' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    answer: text('answer').notNull(),
    isInstructor: boolean('is_instructor').default(false).notNull(),
    isAccepted: boolean('is_accepted').default(false).notNull(),
    upvotes: integer('upvotes').default(0).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    tenantIdIdx: index('lesson_answers_tenant_id_idx').on(table.tenantId),
  }),
)

export const questionUpvotes = pgTable(
  'question_upvotes',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    questionId: uuid('question_id')
      .notNull()
      .references(() => lessonQuestions.id, { onDelete: 'cascade' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    uniqueUserQuestionUpvote: uniqueIndex('unique_user_question_upvote_idx').on(
      table.userId,
      table.questionId,
      table.tenantId,
    ),
    tenantIdIdx: index('question_upvotes_tenant_id_idx').on(table.tenantId),
  }),
)

export const lessonChatMessages = pgTable(
  'lesson_chat_messages',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    lessonId: uuid('lesson_id')
      .notNull()
      .references(() => lessons.id, { onDelete: 'cascade' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    message: text('message').notNull(),
    videoTimestamp: integer('video_timestamp'),
    messageType: text('message_type').default('feedback').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    tenantIdIdx: index('lesson_chat_messages_tenant_id_idx').on(table.tenantId),
  }),
)

export const lessonViewSummary = pgTable(
  'lesson_view_summary',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    lessonId: uuid('lesson_id')
      .notNull()
      .references(() => lessons.id, { onDelete: 'cascade' }),
    date: timestamp('date').notNull(),
    totalViews: integer('total_views').default(0).notNull(),
    uniqueViews: integer('unique_views').default(0).notNull(),
    totalWatchTime: integer('total_watch_time').default(0).notNull(),
    averageWatchTime: real('average_watch_time').default(0).notNull(),
    completionRate: real('completion_rate').default(0).notNull(),
    dropOffPoints: jsonb('drop_off_points'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'cascade' }),
  },
  (table) => ({
    tenantIdIdx: index('lesson_view_summary_tenant_id_idx').on(table.tenantId),
  }),
)

// Type exports for analytics tables
export type LessonFeedback = typeof lessonFeedback.$inferSelect
export type NewLessonFeedback = typeof lessonFeedback.$inferInsert
export type LessonQuestion = typeof lessonQuestions.$inferSelect
export type NewLessonQuestion = typeof lessonQuestions.$inferInsert
export type LessonAnswer = typeof lessonAnswers.$inferSelect
export type NewLessonAnswer = typeof lessonAnswers.$inferInsert
export type QuestionUpvote = typeof questionUpvotes.$inferSelect
export type NewQuestionUpvote = typeof questionUpvotes.$inferInsert
export type LessonChatMessage = typeof lessonChatMessages.$inferSelect
export type NewLessonChatMessage = typeof lessonChatMessages.$inferInsert
export type LessonViewSummary = typeof lessonViewSummary.$inferSelect
export type NewLessonViewSummary = typeof lessonViewSummary.$inferInsert
